/**
 * Legacy-browser feature detection.
 *
 * Some shipping browsers (Safari 12, iOS 12, ancient Edge) crash at
 * parse-time when they see an ES2018 regex with lookbehind `(?<=...)`
 * or named-capture-groups `(?<name>...)`. The error surfaces as
 * "Invalid Regular Expression: Invalid Group Specifier Name" and tears
 * down the entire JS context — the app never gets to its first paint.
 *
 * Several of our markdown dependencies (highlight.js via rehype-highlight,
 * remark-gfm internals) compile such regexes at module load. Loading the
 * `markdown-vendor` chunk on those browsers therefore crashes the page.
 *
 * Strategy:
 *   1. At app boot, try to compile a lookbehind regex inside a try/catch.
 *      Modern engines parse it fine; old engines throw a SyntaxError.
 *   2. Persist the result on globalThis under a stable name.
 *   3. Components that would otherwise statically import the markdown
 *      stack switch to a dynamic-import + plain-text fallback when the
 *      flag is set — so the offending chunk never loads on Safari 12.
 *
 * The detection runs synchronously in main.tsx BEFORE React mounts.
 * That ordering matters: if any component reads the flag during render
 * before boot ran, it would default to "modern" and load the bad chunk.
 */

declare global {
  // eslint-disable-next-line no-var
  var __chaos_legacyRegexBrowser: boolean | undefined;
}

/**
 * Probe the JS engine for ES2018+ regex support. Returns true if the
 * engine fails to compile either lookbehind or named capture groups.
 * Result is cached on globalThis so subsequent calls are constant-time
 * and so React components in different chunks see the same value.
 */
export function detectLegacyRegexBrowser(): boolean {
  const cached = (globalThis as { __chaos_legacyRegexBrowser?: boolean }).__chaos_legacyRegexBrowser;
  if (typeof cached === 'boolean') return cached;

  let legacy = false;
  // Two probes — either failure marks the browser as legacy.
  // Wrapping with eval-like constructor avoids the parser rejecting the
  // file itself at load time on the legacy engine (the literal source
  // `/(?<=x)/` would itself throw at parse time on Safari 12; passing
  // the pattern as a string defers compilation to RegExp()).
  try {
    // eslint-disable-next-line no-new
    new RegExp('(?<=foo)bar');
  } catch {
    legacy = true;
  }
  if (!legacy) {
    try {
      // eslint-disable-next-line no-new
      new RegExp('(?<name>x)');
    } catch {
      legacy = true;
    }
  }

  (globalThis as { __chaos_legacyRegexBrowser?: boolean }).__chaos_legacyRegexBrowser = legacy;
  return legacy;
}

/**
 * Read the cached detection result. Safe to call from anywhere after
 * boot. If called before boot (e.g. during module top-level), runs the
 * detect itself so the answer is never stale.
 */
export function isLegacyRegexBrowser(): boolean {
  const cached = (globalThis as { __chaos_legacyRegexBrowser?: boolean }).__chaos_legacyRegexBrowser;
  if (typeof cached === 'boolean') return cached;
  return detectLegacyRegexBrowser();
}
